import { TrendChartSeries } from '@models';

interface KpiError {
  field?: string;
  error?: string;
}

interface Kpi {
  name: KpiNames;
  type: KpiTypes;
  value?: number;
  numerator?: number;
  numeratorName?: string;
  denominator?: number;
  denominatorName?: string;
  health?: KpiHealth;
  trendText?: string;
  trendIndicator?: TrendIndicatorType;
  trendChange?: number;
  message?: string;
  errorDescription?: KpiError[];
}

interface KpiHistoricalChartData {
  name: KpiNames;
  historicalData?: HistoricalKpi[];
  series: TrendChartSeries[];
  xAxisName: string;
  xAxisLabels: string[];
  yAxisName: string;
  yAxisLabels: string[];
  targetValue?: number;
  type: KpiTypes;
  legend: string[];
}

interface HistoricalKpi {
  value: number;
  date: string;
  type: KpiTypes;
  numerator?: number;
  denominator?: number;
}

interface HistoricalKpiResponse {
  tooltipText?: string;
  data: HistoricalKpi[];
}

interface PortfolioKpiResponse {
  kpis: KpisByKey;
}

interface KpiConfigEntry {
  targetValue: number;
  overTarget: boolean;
}

type KpiConfig = Partial<Record<KpiKeys, KpiConfigEntry>>;

type KpiHealth = `${KpiHealthEnum}`;

type KpiTypes = 'percent' | 'number' | 'days';

type TrendIndicatorType = 'UP_GREEN' | 'DOWN_GREEN' | 'UP_RED' | 'DOWN_RED';

type KpiNames =
  | 'Sites Currently Active'
  | 'Site activation to contract'
  | 'Non-enrolling sites'
  | 'Subject early termination rate'
  | 'Subjects enrolled to contract'
  | 'Subject screen fail rate'
  | 'Deviation rate per subject'
  | 'Days to close site findings'
  | 'CRF review completed'
  | 'Clinical material kits shipped'
  | 'On-time dispatch of clinical materials'
  | 'Key milestones on track to baseline'
  | 'Days to pay sites'
  | 'Source data verification compliance'
  | 'Lab sample query rate per visit'
  | 'eReq usage for lab samples'
  | 'Lab kits shipped'
  | 'Lab kit wastage'
  | 'Critical or major quality events'
  | 'Spend to budget';

type KpiCategories =
  | 'Recruitment & retention'
  | 'Budget & resource utilization'
  | 'Data quality & compliance'
  | 'Timeline & milestone adherence'
  | 'Risk & issue management'
  | 'Portfolio KPIs';

type KpisByKey = Partial<Record<KpiKeys, Kpi>>;

type KpisByCategory = Partial<Record<KpiCategories, KpisByKey>>;

type KpiKeys =
  | 'active'
  | 'enrollment'
  | 'earlyterm'
  | 'enrolled'
  | 'subject_screen_fail_rate'
  | 'deviation_rate_per_subject'
  | 'days_to_close_site_findings'
  | 'crf_review_completed'
  | 'clinical_material_kits_shipped'
  | 'on_time_dispatch_clinical_materials'
  | 'key_milestones_on_track_to_baseline'
  | 'days_to_pay_sites'
  | 'source_data_verification'
  | 'non_enrolling_sites'
  | 'lab_sample_query_rate'
  | 'ereq_usage_for_lab_samples'
  | 'lab_kits_shipped'
  | 'lab_kit_wastage'
  | 'critical_or_major_quality_events'
  | 'spend_to_budget';

enum KpiHealthEnum {
  OnTarget = 'on-target',
  OffTarget = 'off-target',
  AtRisk = 'at-risk',
  Completed = 'completed',
  NoThreshold = 'no-threshold',
  NoData = 'no-data'
}

export { KpiHealthEnum };

export type {
  HistoricalKpi,
  HistoricalKpiResponse,
  Kpi,
  KpiCategories,
  KpiConfig,
  KpiConfigEntry,
  KpiError,
  KpiHealth,
  KpiHistoricalChartData,
  KpiKeys,
  KpiNames,
  KpisByCategory,
  KpisByKey,
  KpiTypes,
  PortfolioKpiResponse,
  TrendIndicatorType
};
